import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Container,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  useMediaQuery,
  useTheme
} from '@mui/material';
import EastIcon from '@mui/icons-material/East';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MedicationIcon from '@mui/icons-material/Medication';
import DevicesIcon from '@mui/icons-material/Devices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { shades } from '../../../../theme';
import mtreatHome from '../../../data/home/mHome.jpg';

const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:900px)');
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const isDarkMode = theme.palette.mode === 'dark';

  // Flat categories list for medical services with links
  const categories = [
    { name: 'Medications', link: '/' },
    { name: 'Healthcare Devices', link: '/' },
    { name: 'First Aid', link: '/' },
    { name: 'Vitamins & Supplements', link: '/' },
    { name: 'Personal Care', link: '/' },
    { name: 'Medical Equipment', link: '/' },
    { name: 'Health Testing', link: '/' }
  ];

  const carouselItems = [
    {
      image: mtreatHome,
      title: 'Essential Medications',
      description: 'Up to 20% off on Prescribed Medicines',
      icon: <MedicationIcon fontSize='large' />
    },
    {
      image: mtreatHome,
      title: 'Health Devices',
      description: 'Advanced health tracking technology for your',
      icon: <DevicesIcon fontSize='large' />
    },
    {
      image: mtreatHome,
      title: 'Medical Assistance',
      description: '24/7 access to medical experts and support',
      icon: <SupportAgentIcon fontSize='large' />
    }
  ];

  const [activeSlide, setActiveSlide] = useState(0);
  const [shopNowHover, setShopNowHover] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const handleNextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % carouselItems.length);
  };

  useEffect(() => {
    const interval = setInterval(handleNextSlide, 5000);
    return () => clearInterval(interval);
  }, []);

  const toggleCategories = () => {
    setShowCategories(prev => !prev);
  };

  return (
    <Box
      sx={{
        width: '100%',
        paddingY: { xs: 2, md: 4 },
        paddingX: { xs: 0, md: 2 },
        backgroundColor: isDarkMode ? shades.secondary[500] : shades.white[100],
        color: isDarkMode ? shades.white[100] : shades.secondary[500],
        borderRadius: 0,
        overflow: 'hidden'
      }}
    >
      <Container maxWidth='xl' disableGutters>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 0
          }}
        >
          {/* Left Side - Categories */}
          <Box
            sx={{
              width: { xs: '100%', md: '25%' },
              padding: 2,
              display: { xs: 'none', md: 'block' },
              backgroundColor: isDarkMode ? shades.secondary[600] : shades.white[200],
              borderRadius: '4px 0 0 4px'
            }}
          >
            <List sx={{ py: 1 }}>
              {categories.map((category, index) => (
                <ListItem
                  key={index}
                  component={Link}
                  href={category.link}
                  underline='none'
                  sx={{
                    py: 1.5,
                    color: isDarkMode ? shades.white[100] : shades.secondary[500],
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: index < categories.length - 1
                      ? `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                      : 'none'
                  }}
                >
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: 500
                    }}
                  />
                  <ChevronRightIcon fontSize='small' />
                </ListItem>
              ))}
            </List>
          </Box>

          {!isMobile && (
            <Divider
              orientation='vertical'
              flexItem
              sx={{
                backgroundColor: isDarkMode
                  ? 'rgba(255,255,255,0.1)'
                  : 'rgba(0,0,0,0.1)',
                height: 'auto',
                my: 0
              }}
            />
          )}

          {/* Right Side - Carousel */}
          <Box
            sx={{
              width: { xs: '100%', md: '75%' },
              position: 'relative',
              height: {
                xs: showCategories ? '500px' : '460px',
                sm: '400px',
                md: '420px'
              },
              overflow: 'hidden',
              borderRadius: { xs: '4px', md: '0 4px 4px 0' },
              backgroundColor: isDarkMode ? shades.secondary[600] : shades.white[200]
            }}
          >
            {carouselItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: activeSlide === index ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  padding: { xs: 2, sm: 3, md: 4 },
                  backgroundColor: isDarkMode ? shades.secondary[600] : shades.white[200]
                }}
              >
                {/* Left side content - Text Area */}
                <Box sx={{
                  width: { xs: '100%', sm: '40%' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: isDarkMode ? shades.white[200] : shades.secondary[500],
                  pr: { xs: 0, sm: 2, md: 4 },
                  mb: { xs: 3, sm: 0 },
                  pb: { xs: 2, sm: 0 },
                  order: { xs: 2, sm: 1 },
                  mt: { xs: 3, sm: 0 }
                }}
                >
                  {/* Icon */}
                  {item.icon && (
                    <Box
                      sx={{
                        mb: { xs: 0, md: 2 },
                        mt: { xs: 2, sm: 0 },
                        color: isDarkMode ? shades.primary[400] : shades.primary[600],
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {item.icon}
                    </Box>
                  )}

                  <Typography
                    variant='h4' component='h2' sx={{
                      color: isDarkMode ? shades.white[100] : shades.secondary[500],
                      mb: 1,
                      fontWeight: 600,
                      fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' }
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant='body1'
                    sx={{
                      mb: { xs: 3, md: 3 },
                      color: isDarkMode ? shades.neutral[400] : shades.neutral[800],
                      fontSize: { xs: '12', sm: '1.5rem', md: '1.75rem' },
                      fontWeight: 500
                    }}
                  >
                    {item.description}
                  </Typography>

                  <Box
                    component={Link}
                    href='/'
                    underline='none'
                    onMouseEnter={() => setShopNowHover(true)}
                    onMouseLeave={() => setShopNowHover(false)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      alignSelf: 'flex-start',
                      borderBottom: `1px solid ${isDarkMode
                        ? (shopNowHover ? shades.primary[400] : 'rgba(255,255,255,0.5)')
                        : (shopNowHover ? shades.primary[600] : 'rgba(0,0,0,0.6)')
                        }`,
                      paddingBottom: '4px',
                      transition: 'all 0.3s ease',
                      color: isDarkMode
                        ? (shopNowHover ? shades.primary[400] : shades.white[200])
                        : (shopNowHover ? shades.primary[600] : shades.secondary[600]),
                      mb: { xs: 2, sm: 0 },
                      position: { xs: 'absolute', sm: 'static' },
                      bottom: { xs: 40, sm: 'auto' },
                      zIndex: 3
                    }}
                  >
                    <Typography
                      component='span'
                      sx={{
                        mr: 0.5,
                        fontWeight: 500,
                        marginTop: { xs: 1, md: 2 }
                      }}
                    >
                      Shop Now
                    </Typography>
                    <EastIcon fontSize='small' sx={{ marginTop: { xs: 1, md: 2 } }} />
                  </Box>
                </Box>

                {/* Right side - Image Area */}
                <Box sx={{
                  width: { xs: '100%', sm: '60%' },
                  height: { xs: '200px', sm: '100%' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  order: { xs: 1, sm: 2 }
                }}
                >
                  <Box
                    component='img'
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: { xs: '100%', sm: '100%', md: '100%' },
                      height: { xs: '100%', sm: '90%', md: '90%' },
                      maxHeight: { xs: '200px', sm: '100%' }, // Increased max height
                      objectFit: 'cover',
                      borderRadius: 'none' // Removed border radius
                    }}
                  />
                </Box>
              </Box>
            ))}

            {/* Carousel Indicators */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 24, md: 16 },
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                zIndex: 2
              }}
            >
              {carouselItems.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: activeSlide === index
                      ? isDarkMode
                        ? shades.primary[400]
                        : shades.primary[600]
                      : isDarkMode
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'rgba(0, 0, 0, 0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: activeSlide === index ? 'scale(1.2)' : 'scale(1)',
                    '&:hover': {
                      backgroundColor: activeSlide !== index &&
                        (isDarkMode
                          ? 'rgba(255, 255, 255, 0.35)'
                          : 'rgba(0, 0, 0, 0.35)'),
                      transform: 'scale(1.1)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
